// EXPEDIENTES (2026-08-13) — wrappers das actions exp_*.
// Tudo entra pelo endpoint POST /comercial/propostas-geradas com body.action,
// que é como ~400 actions do sistema já funcionam. Não há rota própria no API
// Gateway de propósito: o REST API usa recursos explícitos e criar rota nova
// custa deploy de stage (e responde 403 se esquecer).

import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
// Tipos da assinatura: compartilhados com as VENDAS desde 21/08/2026 — o motor
// no backend é um só. Re-exportados no fim do arquivo para não quebrar quem já
// importava daqui.
import type { SignatarioEnvio, Envelope } from '../../components/assinatura/tipos';

export interface TipoExpediente {
  id: number;
  codigo: string;
  prefixo: string;
  nome: string;
  descricao: string | null;
  escopo: 'externo' | 'interno';
  corpo_padrao_html: string;
  campos_schema: CampoSchema[];
  exige_destinatario: boolean;
  sugere_assinatura: boolean;
}

export interface CampoSchema {
  chave: string;
  rotulo: string;
  tipo: 'texto' | 'select' | 'data' | 'numero';
  obrigatorio?: boolean;
  placeholder?: string;
  opcoes?: string[];
}

export interface Envolvido {
  usuario_email: string;
  papel: 'autor' | 'signatario' | 'copia' | 'destinatario_interno';
  ordem: number;
  externo: boolean;
  nome_exibicao: string | null;
  adicionado_em?: string;
}

export interface EventoExpediente {
  tipo: string;
  descricao: string;
  usuario_email: string | null;
  meta: any;
  criado_em: string;
}

// Na listagem, os campos sigilosos vêm NULL para quem não participa —
// `participo` é o que diz se dá pra confiar neles.
export interface ExpedienteLinha {
  id: string;
  codigo: string | null;
  status: 'rascunho' | 'finalizado' | 'cancelado';
  data_documento: string | null;
  criado_em: string;
  numerado_em: string | null;
  tipo_codigo: string;
  tipo_nome: string;
  prefixo: string;
  escopo: string;
  participo: boolean;
  assunto: string | null;
  destinatario_nome: string | null;
  destinatario_org: string | null;
  criado_por: string | null;
  assinatura_status: string | null;
  cancelado_motivo: string | null;
  envolvidos: { email: string; papel: string; nome: string | null }[] | null;
}

export interface Expediente extends ExpedienteLinha {
  local_emissao: string | null;
  destinatario_cargo: string | null;
  destinatario_endereco: string | null;
  cliente_id: number | null;
  contrato_id: string | null;
  negocio_id: number | null;
  dados_form: Record<string, any>;
  corpo_html: string;
  corpo_final_html: string | null;
  campos_schema: CampoSchema[];
  exige_destinatario: boolean;
  /** 'expediente' | 'juridico' — contrato assina como venda e salva template. */
  area?: string;
  /** Do TIPO: o memorando sai sem linha de assinatura (a menos que vá para a D4 Sign). */
  sugere_assinatura?: boolean;
  tipo_sensivel?: boolean;
  substitui_id: string | null;
  substituido_por_id: string | null;
  numero_seq: number | null;
  pdf_s3_key: string | null;
}

async function chamar<T = any>(action: string, body: Record<string, any> = {}): Promise<T> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`,
    { action, ...body },
    { headers: { Authorization: token } });
  // A API às vezes devolve envelopada em `body` (stringificada), às vezes não.
  return r.data.body ? JSON.parse(r.data.body) : r.data;
}

export const listarTipos      = () => chamar<{ tipos: TipoExpediente[] }>('exp_tipos');
export const salvarTipo       = (t: Partial<TipoExpediente>) => chamar('exp_tipo_salvar', t as any);

export const listarExpedientes = (f: {
  ano?: number | null; tipo_id?: number | null; status?: string | null;
  so_meus?: boolean; busca?: string;
} = {}) => chamar<{ expedientes: ExpedienteLinha[]; ver_todos: boolean }>('exp_listar', f);

export const obterExpediente = (id: string) => chamar<{
  expediente: Expediente; envolvidos?: Envolvido[]; eventos?: EventoExpediente[];
  participo: boolean; pode_editar: boolean; por_ver_todos?: boolean;
}>('exp_obter', { id });

export const criarExpediente  = (tipo_id: number) =>
  chamar<{ expediente: { id: string } }>('exp_criar', { tipo_id });

export const atualizarExpediente = (id: string, campos: Record<string, any>) =>
  chamar('exp_atualizar', { id, ...campos });

export const excluirExpediente = (id: string) => chamar('exp_excluir', { id });

/** Pam redige o corpo a partir de um pedido em português corrido.
 *  NÃO grava: devolve o HTML para a tela mostrar, e quem decide usar é quem
 *  está escrevendo. `faltas` traz os marcadores {{FALTA: ...}} que a Pam
 *  deixou onde faltou dado — é o que ela NÃO inventou. */
export const redigirComIA = (id: string, prompt: string, modo: 'substituir' | 'complementar') =>
  chamar<{ html: string; faltas: string[]; tokens: { entrada: number; saida: number } }>(
    'exp_redigir', { id, prompt, modo });

export const adicionarEnvolvido = (id: string, p: {
  usuario_email: string; papel: string; externo?: boolean; nome_exibicao?: string; ordem?: number;
}) => chamar('exp_envolvido_add', { id, ...p });

export const removerEnvolvido = (id: string, usuario_email: string, papel?: string) =>
  chamar('exp_envolvido_remover', { id, usuario_email, papel });

export const previewExpediente = (id: string) => chamar<{
  corpo_html: string; variaveis_pendentes: string[]; campos_faltando: string[]; ctx: any;
}>('exp_preview', { id });

export const finalizarExpediente = (id: string) =>
  chamar<{ codigo: string; data_documento: string }>('exp_finalizar', { id });

export const cancelarExpediente = (id: string, motivo: string) =>
  chamar('exp_cancelar', { id, motivo });

export const substituirExpediente = (id: string) =>
  chamar<{ id: string; substitui: string }>('exp_substituir', { id });

export const pdfExpediente = (id: string) =>
  chamar<{ url: string; minuta: boolean }>('exp_pdf', { id });

// O PDF ASSINADO não pode ser regenerado — é o arquivo que a D4 Sign devolveu,
// com a página de assinaturas e a assinatura criptográfica. Buscar pelo
// exp_pdf comum traz uma regeneração limpa, sem assinatura.
export const pdfAssinado = (id: string) =>
  chamar<{ url: string; assinado: boolean }>('exp_pdf', { id, assinado: true });

// ─── Assinatura digital (onda 2) ──────────────────────────────────────────
// Os tipos vivem em components/assinatura/tipos.ts desde 21/08/2026 — são os
// mesmos que as VENDAS usam (o motor no backend é um só). Re-exportados aqui
// para não quebrar quem já importava deste arquivo.
export type { SignatarioEnvio, SignatarioStatus, Envelope } from '../../components/assinatura/tipos';
export { QUALIFICACOES } from '../../components/assinatura/tipos';

export const enviarParaAssinatura = (id: string, p: {
  signatarios: SignatarioEnvio[]; workflow?: boolean; mensagem?: string;
}) => chamar<{ envelope_id: string; signatarios: number }>('exp_assinar', { id, ...p });

// Lê do NOSSO banco (quem fala com a D4 Sign é o cron) — pode chamar à vontade.
export const statusAssinatura = (id: string) => chamar<{
  envelope: Envelope | null; progresso?: { assinados: number; total: number };
}>('exp_assinatura_status', { id });

// Consulta a D4 Sign NA HORA (o cron é o piso, de 15 min). Devolve o mesmo
// formato do statusAssinatura, já atualizado — dá para trocar o estado direto.
// Recusa com 429 se alguém acabou de consultar: a cota padrão da conta é de
// 10 requisições/hora, e clique repetido custa o envio de outra pessoa.
export const verificarAssinatura = (id: string) => chamar<{
  envelope: Envelope | null; progresso?: { assinados: number; total: number };
}>('exp_assinatura_verificar', { id });

export const cancelarAssinatura = (id: string, motivo?: string) =>
  chamar('exp_assinatura_cancelar', { id, motivo });
/** Conclui um envio que FALHOU depois de o documento já ter subido ao cofre da
 *  D4 Sign, sem subir outra vez. Reenviar pelo caminho normal criaria uma
 *  segunda via assinável — o servidor recusa com 409 (30/09/2026). */
export const retomarAssinatura = (id: string) =>
  chamar<{ retomado: boolean; tentativa: number; etapa: string | null }>(
    'exp_assinatura_retomar', { id });

export const ENVELOPE_CFG: Record<string, { label: string; cor: string }> = {
  rascunho:              { label: 'Preparando',            cor: 'default' },
  processando_envio:     { label: 'Enviando…',             cor: 'processing' },
  aguardando_assinatura: { label: 'Aguardando assinatura', cor: 'gold' },
  assinado:              { label: 'Assinado',              cor: 'green' },
  cancelado:             { label: 'Cancelado',             cor: 'red' },
  erro_envio:            { label: 'Falhou no envio',       cor: 'red' },
  erro_consulta:         { label: 'Erro ao consultar',     cor: 'orange' },
};

// Vocabulário de variáveis do editor de expediente. Pequeno e interno de
// propósito — nada de cliente/composição, que é vocabulário de proposta.
export const VARIAVEIS_EXPEDIENTE = [
  { grupo: 'Empresa', vars: ['empresa.razao_social', 'empresa.cnpj', 'empresa.endereco_completo', 'empresa.crea'] },
  { grupo: 'Documento', vars: ['doc.codigo', 'doc.data_extenso', 'doc.assunto', 'doc.local_emissao'] },
  { grupo: 'Destinatário', vars: ['destinatario.nome', 'destinatario.cargo', 'destinatario.organizacao'] },
  { grupo: 'Autor', vars: ['autor.nome', 'autor.cargo', 'autor.crea'] },
];

export const STATUS_CFG: Record<string, { label: string; cor: string }> = {
  rascunho:   { label: 'Rascunho',   cor: 'default' },
  finalizado: { label: 'Finalizado', cor: 'green' },
  cancelado:  { label: 'Cancelado',  cor: 'red' },
};
